import { FC, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
    FlexRow,
    LinkContainer,
    SideMenuLink,
    SvgIcon,
    TruncatedTextWithTooltip,
    TruncatedTextWithTooltipContentProps,
} from '@portal/common/components';
import { styled } from '@portal/common/theme';
import { ResourceList } from '@portal/common/types';

import { FAVORITE_CONTENT_PATH, MY_LISTS_PATH, PUBLIC_LISTS_PATH } from '../../../router/paths';
import { getSessionAccountId } from '../../../store/session/selectors';

type Props = {
    list: Partial<ResourceList>;
    isPublic?: boolean;
    resourceCount?: number;
};

const SidenavNavLink = styled(SideMenuLink)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    height: 'auto',
    color: theme.color.gray40,
    ':hover, :active, &.active': {
        color: theme.color.green2,
        svg: {
            color: theme.color.green2,
        },
    },
}));

const StyledIcon = styled(SvgIcon)({
    marginRight: 10,
    verticalAlign: 'top',
});

const ContentRow = styled(FlexRow)({
    minWidth: 0,
    width: '100%',
});

const ListItem: FC<Props> = ({ list, resourceCount, isPublic, ...props }) => {
    const memberId = useSelector(getSessionAccountId);
    const path = `${FAVORITE_CONTENT_PATH}${isPublic ? PUBLIC_LISTS_PATH : MY_LISTS_PATH}`;
    const count = list.resource_ids ? list.resource_ids?.length : 0;
    const isCreator = list.creator && list.creator.id !== memberId;
    const icon = useMemo(
        () =>
            !isPublic && list.is_public && isCreator ? (
                <StyledIcon size="md" src="/icons/ic-team.svg" />
            ) : null,
        [isPublic, list.is_public, isCreator]
    );
    const tooltipText = `${list.name}\n${list.description ? list.description : ''}`.trim();

    const renderContent = useCallback(
        (contentProps: TruncatedTextWithTooltipContentProps) => (
            <ContentRow align="center">
                {icon}
                <span {...contentProps}>{list.name}</span>
            </ContentRow>
        ),
        [icon, list.name]
    );

    return (
        <LinkContainer key={list.id} to={path + `/${list.id}`}>
            <SidenavNavLink {...props}>
                <TruncatedTextWithTooltip
                    tooltipText={tooltipText}
                    shouldAlwaysShowTooltip={!!list.description}
                    renderContent={renderContent}
                />
                <div>{resourceCount ?? count}</div>
            </SidenavNavLink>
        </LinkContainer>
    );
};

export default ListItem;
